import { useState, FunctionComponent } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFragment, graphql, RelayProp } from 'react-relay';
import pick from 'lodash.pick';
import { FormattedMessage } from 'dibs-react-intl';
import { ControlledAddressForm } from 'dibs-address-form';
import { validate as addressValidator } from 'dibs-address-validator/exports/validate';
import { Spinner } from 'dibs-elements/exports/Spinner';
import { Checkbox } from 'dibs-elements/exports/Checkbox';
import CheckoutCTAButton from '../../components/shared/CheckoutCTAButton';
import { toggleBillingAddress } from '../../actions/checkoutPaymentActions';
import {
    saveKlarnaPaymentMethod,
    klarnaBillingFormFieldUpdated,
} from '../../actions/checkoutKlarnaActions';
import { billingFields } from '../../helpers/checkoutForms';
import { authorizeKlarnaPayment } from 'dibs-klarna/exports/klarnaSdk';
import {
    useCheckoutKlarnaSession,
    useKlarnaPaymentsWidget,
    useKlarnaSessionUpdateMutation,
} from '../../helpers/klarna';
import type { BillingAddress } from '../../helpers/klarna';
import { trackKlarnaInteraction } from '../../helpers/checkoutTracking';
import styles from './styles/CheckoutKlarnaPayment.scss';

import { CheckoutKlarnaPayment_buyer$key } from './__generated__/CheckoutKlarnaPayment_buyer.graphql';
import { CheckoutKlarnaPayment_transaction$key } from './__generated__/CheckoutKlarnaPayment_transaction.graphql';
import { CheckoutKlarnaPayment_viewer$key } from './__generated__/CheckoutKlarnaPayment_viewer.graphql';

type ReduxState = {
    payment?: {
        isBillingSameAsShipping?: boolean;
        newBillingForm?: BillingAddress;
        klarnaFailedReauthorization?: boolean;
    };
    transaction?: {
        transactionId?: string | null;
        shippingAddressId?: string | null;
    };
} | null;

type CheckoutKlarnaPaymentProps = {
    buyer: CheckoutKlarnaPayment_buyer$key;
    transaction: CheckoutKlarnaPayment_transaction$key;
    viewer: CheckoutKlarnaPayment_viewer$key;
    wrapperClass: string;
    relay: RelayProp;
    isComputing: boolean;
};

export const CheckoutKlarnaPayment: FunctionComponent<CheckoutKlarnaPaymentProps> = props => {
    const { wrapperClass, relay, isComputing } = props;
    const dispatch = useDispatch();

    const [isAuthorizing, setIsAuthorizing] = useState(false);
    const [hasChangedBillingAddress, setHasChangedBillingAddress] = useState(false);
    const [hasFailedAuthorization, setHasFailedAuthorization] = useState(false);

    const transactionId = useSelector<ReduxState, string | null>(
        state => state?.transaction?.transactionId || null
    );
    const isBillingSameAsShipping = useSelector<ReduxState, boolean>(
        state => !!state?.payment?.isBillingSameAsShipping
    );
    const newBillingForm = useSelector<ReduxState, BillingAddress>(
        state => state?.payment?.newBillingForm || {}
    );
    const shippingAddressId = useSelector<ReduxState, string | null>(
        state => state?.transaction?.shippingAddressId || null
    );
    const klarnaFailedReauthorization = useSelector<ReduxState, boolean>(
        state => !!state?.payment?.klarnaFailedReauthorization
    );

    const viewer = useFragment(
        graphql`
            fragment CheckoutKlarnaPayment_viewer on Viewer {
                ...ControlledAddressForm_viewer
            }
        `,
        props.viewer
    );

    const transaction = useFragment(
        graphql`
            fragment CheckoutKlarnaPayment_transaction on Transaction {
                buyerInvoice {
                    invoiceItems {
                        code
                        totalAmount {
                            amount
                        }
                    }
                }
            }
        `,
        props.transaction
    );

    const buyer = useFragment(
        graphql`
            fragment CheckoutKlarnaPayment_buyer on User {
                addressBook {
                    addressList {
                        serviceId
                        firstName
                        lastName
                        country
                        line1
                        line2
                        city
                        state
                        zipCode
                        phoneNumber
                    }
                }
            }
        `,
        props.buyer
    );

    const shippingAddress = buyer.addressBook?.addressList?.find(
        address => address?.serviceId === shippingAddressId
    );

    const billingAddress = isBillingSameAsShipping
        ? pick(shippingAddress, [...billingFields, 'phoneNumber'])
        : newBillingForm;
    const { klarnaSessionId, klarnaClientToken, hasKlarnaSessionError } = useCheckoutKlarnaSession({
        transactionId,
        billingAddress,
        buyerInvoice: transaction.buyerInvoice,
    });
    const [updateKlarnaSession] = useKlarnaSessionUpdateMutation(transactionId, klarnaSessionId);

    const widgetContainerId = 'checkout-klarna-payments-container';
    const { hasLoadedKlarnaWidget, showKlarnaForm } = useKlarnaPaymentsWidget(
        klarnaClientToken,
        '#' + widgetContainerId
    );

    function handleBillingAddressToggle(): void {
        dispatch(toggleBillingAddress(!isBillingSameAsShipping));
        setHasChangedBillingAddress(true);
    }

    function handleBillingAddressUpdate(event: { field: string; value: string }): void {
        const { field, value } = event;
        dispatch(klarnaBillingFormFieldUpdated(field, value));
        setHasChangedBillingAddress(true);
    }

    async function handleAuthorize(): Promise<void> {
        if (!klarnaSessionId) {
            return;
        }

        trackKlarnaInteraction('klarna continue clicked', { isInteractive: true });

        setIsAuthorizing(true);

        if (hasChangedBillingAddress) {
            await updateKlarnaSession(billingAddress);
            setHasChangedBillingAddress(false);
        }

        const { approved, showForm, authorizationToken, finalizeRequired } =
            await authorizeKlarnaPayment();
        setIsAuthorizing(false);

        if (approved) {
            trackKlarnaInteraction('klarna success', { isInteractive: false });
            dispatch(
                saveKlarnaPaymentMethod({
                    sessionId: klarnaSessionId,
                    authorizationToken: authorizationToken,
                    billingAddress: billingAddress,
                    klarnaNeedsFinalization: finalizeRequired,
                    buyerInvoice: transaction.buyerInvoice,
                })
            );
        } else if (!showForm) {
            trackKlarnaInteraction('klarna failure', { isInteractive: false });
            setHasFailedAuthorization(true);
        }
    }

    const addressValidations = {
        allowPOBox: true,
    };
    const isBillingAddressValid =
        isBillingSameAsShipping || addressValidator(newBillingForm, addressValidations).isValid;
    const isDisabled =
        isComputing || isAuthorizing || (!isBillingSameAsShipping && !isBillingAddressValid);

    const isLoading = !hasLoadedKlarnaWidget && !hasKlarnaSessionError;
    const isKlarnaUnavailable =
        (hasLoadedKlarnaWidget && !showKlarnaForm) ||
        hasKlarnaSessionError ||
        hasFailedAuthorization ||
        klarnaFailedReauthorization;

    return (
        <div className={wrapperClass}>
            {!isKlarnaUnavailable && <div id={widgetContainerId} />}
            {isKlarnaUnavailable && (
                <div className={styles.error}>
                    <FormattedMessage
                        id="checkout.KlarnaPaymentMethod.unavailableMessage"
                        defaultMessage="Unfortunately, Klarna is not available for this order now. Please select another payment method to proceed with checkout."
                    />
                </div>
            )}
            {isLoading ? (
                <div className={styles.loader}>
                    <Spinner />
                </div>
            ) : (
                showKlarnaForm &&
                !hasFailedAuthorization && (
                    <>
                        <div className={styles.billingAddress}>
                            <Checkbox
                                name="isBillingSameAsShipping"
                                label={
                                    <FormattedMessage
                                        id="checkout.KlarnaPaymentMethod.sameAsShipping"
                                        defaultMessage="Billing Address is the Same as Shipping Address"
                                    />
                                }
                                checked={isBillingSameAsShipping}
                                onChange={handleBillingAddressToggle}
                                dataTn="same-as-shipping-address-checkbox"
                                disabled={isAuthorizing || isComputing}
                            />
                        </div>
                        <div className={styles.addressForm}>
                            {!isBillingSameAsShipping && (
                                <ControlledAddressForm
                                    relay={relay}
                                    viewer={viewer}
                                    address={newBillingForm}
                                    hidePhoneNumberTip
                                    hideCompany
                                    hideSubmit
                                    validations={addressValidations}
                                    disableForm={isAuthorizing || isComputing}
                                    onUpdate={handleBillingAddressUpdate}
                                />
                            )}
                        </div>
                        <CheckoutCTAButton
                            dataTn="klarna-authorize-button"
                            onClick={handleAuthorize}
                            disabled={isDisabled}
                            isBlocked={isDisabled}
                            isSaving={isAuthorizing}
                            label={
                                <FormattedMessage
                                    id="checkout.KlarnaPaymentMethod.payWithKlarna"
                                    defaultMessage="Pay with Klarna"
                                />
                            }
                        />
                    </>
                )
            )}
        </div>
    );
};
